import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Users, Calendar, MessageCircle, MapPin, PartyPopper, Search,
    LayoutGrid, Settings, UserCircle, LogOut, BarChart3, ChevronLeft, ChevronRight,
    Briefcase, Folder, X, Building2, HeadphonesIcon, Minus, Globe, ChevronDown
} from 'lucide-react';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { getStoredUser } from '../../hooks/useAuth';
import { logout } from '../../services/authService';
import logo from '../../assets/logo.png';
import { getPeoplePreferences, savePeoplePreferences } from '../../services/apiService';
import { getModules, getPeopleModules, MODULE_NAV_MAP } from '../../services/modulesService';
import { getUnreadCount } from '../../services/messagesService';
import MouseGlow from '../ui/MouseGlow';

// Module icons mapping
const MODULE_ICONS = {
    'CONTACTS': Users,
    'CONVERSATIONS': MessageCircle,
    'MAP': MapPin,
    'AGENDA': Calendar,
    'EVENTS': PartyPopper,
    'PLACES': Building2,
    'SUPPORT': HeadphonesIcon,
    'WORK': Briefcase
};

// Module colors
const MODULE_COLORS = {
    'CONTACTS': '#3b82f6',
    'CONVERSATIONS': '#8b5cf6',
    'MAP': '#22c55e',
    'AGENDA': '#f97316',
    'EVENTS': '#ec4899',
    'PLACES': '#14b8a6',
    'SUPPORT': '#6366f1',
    'WORK': '#0ea5e9'
};

// Nav ID to code mapping
const NAV_TO_CODE = {
    'contacts': 'CONTACTS',
    'messages': 'CONVERSATIONS',
    'map': 'MAP',
    'calendar': 'AGENDA',
    'events': 'EVENTS',
    'places': 'PLACES',
    'support': 'SUPPORT',
    'work': 'WORK'
};

// Fixed dock apps (always present, not movable)
const DOCK_APPS = [
    { id: 'profile', icon: UserCircle, color: '#6366f1', navId: 'profile' },
    { id: 'modules', icon: LayoutGrid, color: '#8b5cf6', navId: 'modules' },
    { id: 'search', icon: Search, color: '#f59e0b', navId: 'search' },
    { id: 'preferences', icon: Settings, color: '#64748b', navId: 'preferences' }
];

// Background presets
const BACKGROUNDS = {
    cabaza: { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' },
    iphone: { gradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' },
    android: { gradient: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
    nature: { image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80' },
    aurora: { gradient: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
    sunset: { gradient: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 50%, #ff6a88 100%)' },
    ocean: { gradient: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)' }
};

// Helper to normalize keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    return Object.keys(obj).reduce((acc, key) => {
        const lowerKey = key.toLowerCase();
        acc[lowerKey] = normalizeKeys(obj[key]);
        return acc;
    }, {});
};

/**
 * Dashboard Language Switcher - Style adapté au dashboard
 */
const DashboardLanguageSwitcher = () => {
    const { language, setLanguage, languages } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (langCode) => {
        setLanguage(langCode);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors"
            >
                <Globe className="w-4 h-4" />
                <span className="uppercase">{currentLanguage.code}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-1 min-w-[150px] z-[9999]">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                                lang.code === language ? 'bg-purple-50 text-purple-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                            <span className="w-5 text-center font-medium text-xs uppercase opacity-60">{lang.flag}</span>
                            <span>{lang.name}</span>
                            {lang.code === language && (
                                <span className="ml-auto w-2 h-2 rounded-full bg-purple-500" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Group Popup Modal - Shows modules inside a group
 */
const GroupPopupModal = ({ group, onClose, onNavigate, unreadMessages, t, language }) => {
    const popupRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    if (!group) return null;

    // Filter modules that are pinned/visible
    const visibleModules = (group.modules || []).filter(m => {
        const isPinned = m.ispinned === undefined || m.ispinned === null ||
                        m.ispinned === true || m.ispinned === 1 || m.ispinned === '1';
        return isPinned;
    });

    const getModuleIcon = (code) => MODULE_ICONS[code] || LayoutGrid;
    const getNavId = (code) => MODULE_NAV_MAP[code] || code.toLowerCase();

    return createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
            <div
                ref={popupRef}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-4 text-white"
                    style={{ backgroundColor: group.color || '#8b5cf6' }}
                >
                    <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6" />
                        <span className="font-semibold text-lg">{group.name}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modules Grid */}
                <div className="p-4">
                    {visibleModules.length === 0 ? (
                        <p className="text-center text-gray-400 py-8">
                            {t.modules?.noModules || 'No modules in this group'}
                        </p>
                    ) : (
                        <div className="grid grid-cols-3 gap-4">
                            {visibleModules.map((module) => {
                                const Icon = getModuleIcon(module.code);
                                const navId = getNavId(module.code);
                                const color = MODULE_COLORS[module.code] || '#8b5cf6';
                                const hasBadge = module.code === 'CONVERSATIONS' && unreadMessages > 0;

                                return (
                                    <button
                                        key={module.itemid || module.moduleid}
                                        onClick={() => {
                                            onNavigate(navId);
                                            onClose();
                                        }}
                                        className="flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-gray-100 transition-colors"
                                    >
                                        <div
                                            className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                                            style={{ backgroundColor: color }}
                                        >
                                            <Icon className="w-7 h-7 text-white" />
                                            {hasBadge && (
                                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-700 font-medium text-center leading-tight max-w-[70px] truncate">
                                            {module.name || module.label || module[`name_${language}`]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

/**
 * Dashboard - Smartphone-style home screen with app icons
 */
const Dashboard = ({ onNavigate }) => {
    const { t, language } = useTranslation();
    const user = getStoredUser();
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Background preference
    const [background, setBackground] = useState(() => {
        return localStorage.getItem('cabazaDashboardBackground') || 'cabaza';
    });

    // Pinned items from sidebar (modules and groups)
    const [pinnedItems, setPinnedItems] = useState(() => {
        const stored = localStorage.getItem('cabazaPinnedItems');
        return stored ? JSON.parse(stored) : [];
    });

    // Groups data from sidebar
    const [groupsData, setGroupsData] = useState(() => {
        const stored = localStorage.getItem('cabazaGroupsData');
        return stored ? JSON.parse(stored) : [];
    });

    // All modules from API
    const [allModules, setAllModules] = useState([]);

    // Unread messages count
    const [unreadMessages, setUnreadMessages] = useState(0);

    // Open group popup
    const [openGroupPopup, setOpenGroupPopup] = useState(null);

    // Multi-desktop support: desktops is an array of arrays of pinned item identifiers
    // Format: [["module:contacts", "group:123", "module:messages"], [...]]
    const [desktops, setDesktops] = useState(() => {
        const stored = localStorage.getItem('cabazaDashboardDesktops');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch (e) { }
        }
        return [[]]; // Start with empty, will be populated from pinnedItems
    });

    // Current desktop index
    const [currentDesktop, setCurrentDesktop] = useState(0);


    // Drag state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [draggedId, setDraggedId] = useState(null);

    // Touch drag state
    const [touchDragInfo, setTouchDragInfo] = useState(null);
    const touchStartPos = useRef(null);
    const longPressTimer = useRef(null);
    const appRefs = useRef({});

    // Save timeout ref for debouncing
    const saveTimeoutRef = useRef(null);

    // Edge drag zone state
    const [edgeDragZone, setEdgeDragZone] = useState(null);
    const edgeDragTimer = useRef(null);

    // Drop target state
    const [dropTarget, setDropTarget] = useState(null);

    // Edge hover state (for showing arrows on mouse hover near edges)
    const [edgeHover, setEdgeHover] = useState(null); // 'left' | 'right' | null

    // Get current desktop apps (filtered by pinned items)
    const gridApps = desktops[currentDesktop] || [];

    // Track if data has been loaded
    const dataLoadedRef = useRef(false);

    // Load data on mount (only once)
    useEffect(() => {
        if (dataLoadedRef.current || !userId) return;
        dataLoadedRef.current = true;

        const loadData = async () => {
            try {
                const [modulesData, groupsDataFromApi, prefs, count] = await Promise.all([
                    getModules(language),
                    getPeopleModules(userId, language),
                    getPeoplePreferences(userId),
                    getUnreadCount()
                ]);

                const normalizedModules = normalizeKeys(modulesData);
                const normalizedGroups = normalizeKeys(groupsDataFromApi);

                setAllModules(normalizedModules);
                setGroupsData(normalizedGroups);
                setUnreadMessages(count);
                localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));

                // Load pinned items from API
                const pinnedItemsFromApi = prefs.pinnedItems || prefs.pinneditems || [];
                if (pinnedItemsFromApi.length > 0) {
                    setPinnedItems(pinnedItemsFromApi);
                    localStorage.setItem('cabazaPinnedItems', JSON.stringify(pinnedItemsFromApi));
                }

                // Load dashboard layout from API
                const dashboardDesktops = prefs.dashboardDesktops || prefs.dashboarddesktops;

                if (dashboardDesktops && Array.isArray(dashboardDesktops) && dashboardDesktops.length > 0) {
                    setDesktops(dashboardDesktops);
                    localStorage.setItem('cabazaDashboardDesktops', JSON.stringify(dashboardDesktops));
                }
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            }
        };
        loadData();
    }, [userId]);

    // Listen for changes from sidebar/modules page
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            const stored = localStorage.getItem('cabazaPinnedItems');
            setPinnedItems(stored ? JSON.parse(stored) : []);
        };

        const handleGroupsChange = async () => {
            const stored = localStorage.getItem('cabazaGroupsData');
            if (stored) {
                setGroupsData(JSON.parse(stored));
            } else if (userId) {
                const groupsDataFromApi = await getPeopleModules(userId, language);
                const normalizedGroups = normalizeKeys(groupsDataFromApi);
                setGroupsData(normalizedGroups);
                localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));
            }
        };

        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedModulesChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
        window.addEventListener('modulesGroupsChanged', handleGroupsChange);
        window.addEventListener('modulePinnedToggled', handleGroupsChange);

        return () => {
            window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedModulesChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
            window.removeEventListener('modulesGroupsChanged', handleGroupsChange);
            window.removeEventListener('modulePinnedToggled', handleGroupsChange);
        };
    }, [userId, language]);

    // Max apps per desktop (4x3 grid)
    const MAX_APPS_PER_DESKTOP = 12;

    // Sync desktops with pinned items when they change
    // Skip initial sync if we already have data from localStorage/API
    const initialSyncDoneRef = useRef(false);

    useEffect(() => {
        // Skip if pinnedItems is empty (not loaded yet)
        if (pinnedItems.length === 0) return;

        // Convert pinned items to identifiers
        const pinnedIds = pinnedItems.map(item => `${item.type}:${item.id}`);
        const pinnedIdSet = new Set(pinnedIds);

        // Check if we already have layout data
        const hasExistingLayout = desktops.some(d => d.length > 0);

        if (hasExistingLayout && initialSyncDoneRef.current) {
            // Filter desktops to only include pinned items (removes unpinned items, keeping order)
            let newDesktops = desktops.map(desktop =>
                desktop.filter(id => pinnedIdSet.has(id))
            );

            // Find items that are pinned but not yet placed anywhere
            const allPlacedIds = new Set(newDesktops.flat());
            const unplacedItems = pinnedIds.filter(id => !allPlacedIds.has(id));

            // Add unplaced items to the appropriate desktop
            if (unplacedItems.length > 0) {
                if (newDesktops.length === 0) newDesktops = [[]];

                for (const itemId of unplacedItems) {
                    let placed = false;
                    for (let i = 0; i < newDesktops.length; i++) {
                        if (newDesktops[i].length < MAX_APPS_PER_DESKTOP) {
                            newDesktops[i].push(itemId);
                            placed = true;
                            break;
                        }
                    }
                    if (!placed) {
                        console.log('No space available for new pinned item:', itemId);
                    }
                }
            }

            // Remove empty desktops (except first one)
            newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
            if (newDesktops.length === 0) newDesktops = [[]];

            // Update state if changed
            if (JSON.stringify(newDesktops) !== JSON.stringify(desktops)) {
                setDesktops(newDesktops);
                saveToDb(newDesktops);

                if (currentDesktop >= newDesktops.length) {
                    setCurrentDesktop(Math.max(0, newDesktops.length - 1));
                }
            }
        } else if (!hasExistingLayout) {
            // No existing layout - initialize from pinnedItems
            const initialDesktop = pinnedIds.slice(0, MAX_APPS_PER_DESKTOP);
            if (initialDesktop.length > 0) {
                setDesktops([initialDesktop]);
                saveToDb([initialDesktop]);
            }
        }

        initialSyncDoneRef.current = true;
    }, [pinnedItems]);

    // Prevent scrolling while dragging
    useEffect(() => {
        const preventScroll = (e) => {
            if (touchDragInfo) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventScroll, { passive: false });
        return () => document.removeEventListener('touchmove', preventScroll);
    }, [touchDragInfo]);

    // Save to DB (debounced)
    const saveToDb = (newDesktops) => {
        localStorage.setItem('cabazaDashboardDesktops', JSON.stringify(newDesktops));

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(async () => {
            if (!userId) return;
            try {
                await savePeoplePreferences(userId, {
                    dashboardDesktops: newDesktops
                });
            } catch (error) {
                console.error('Error saving dashboard preferences:', error);
            }
        }, 500);
    };

    // Get current background
    const currentBg = BACKGROUNDS[background] || BACKGROUNDS.cabaza;

    // Handle logout
    const handleLogout = async () => {
        await logout();
        localStorage.removeItem('authToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        localStorage.removeItem('cabaza_dashboard_page');
        window.location.href = '/';
    };

    // Get group by id
    const getGroupById = (groupId) => {
        return groupsData.find(g => g.id === groupId || g.id === parseInt(groupId));
    };

    // Get module label
    const getModuleLabel = (navId) => {
        const labels = {
            contacts: t.menu?.contacts || 'Contacts',
            messages: t.menu?.messages || 'Messages',
            map: t.menu?.map || 'Carte',
            calendar: t.menu?.calendar || 'Agenda',
            events: t.menu?.events || 'Events',
            places: t.menu?.places || 'Endroits',
            support: t.menu?.support || 'Support',
            work: t.menu?.work || 'Demandes',
            offers: t.menu?.offers || 'Offres'
        };
        return labels[navId] || navId;
    };

    // Get dock app label
    const getDockAppLabel = (appId) => {
        const labels = {
            profile: t.menu?.profile || 'Profil',
            preferences: t.menu?.preferences || 'Préférences',
            search: t.menu?.search || 'Recherche',
            modules: t.menu?.modules || 'Modules'
        };
        return labels[appId] || appId;
    };

    // Parse item identifier (e.g., "module:contacts" or "group:123")
    const parseItemId = (itemId) => {
        if (!itemId || typeof itemId !== 'string') return null;
        const [type, id] = itemId.split(':');
        return { type, id: type === 'group' ? parseInt(id) : id };
    };

    // ========== DRAG & DROP (grid only) ==========

    const handleDragStart = (e, position) => {
        dragItem.current = { position };
        setDraggedId(gridApps[position]);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, position) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const side = e.clientX < midX ? 'left' : 'right';
        dragOverItem.current = { position, side };
        setDropTarget({ position, side });
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDropTarget(null);
        }
    };

    const handleContainerDragOver = (e) => {
        e.preventDefault();
        if (!dropTarget) {
            dragOverItem.current = { position: gridApps.length, side: 'left' };
            setDropTarget({ position: gridApps.length, side: 'left' });
        }
        // Check edge zones for desktop drag
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (!dragItem.current) {
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current || { position: 0, side: 'left' };

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        if (from.position === insertPosition || from.position === insertPosition - 1) {
            resetDrag();
            return;
        }

        let newDesktops = desktops.map(d => [...d]);
        let newGridApps = [...newDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        newDesktops[currentDesktop] = newGridApps;
        setDesktops(newDesktops);
        saveToDb(newDesktops);
        resetDrag();
    };

    const resetDrag = () => {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggedId(null);
        setDropTarget(null);
        setEdgeDragZone(null);
        if (edgeDragTimer.current) {
            clearTimeout(edgeDragTimer.current);
            edgeDragTimer.current = null;
        }
    };

    const handleDragEnd = () => resetDrag();

    // Move app to another desktop
    const moveAppToDesktop = (appId, fromPosition, targetDesktopIndex) => {
        let newDesktops = desktops.map(d => [...d]);

        newDesktops[currentDesktop] = newDesktops[currentDesktop].filter((_, i) => i !== fromPosition);

        while (newDesktops.length <= targetDesktopIndex) {
            newDesktops.push([]);
        }
        newDesktops[targetDesktopIndex].push(appId);
        newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);

        setDesktops(newDesktops);
        saveToDb(newDesktops);

        const newTargetIndex = Math.min(targetDesktopIndex, newDesktops.length - 1);
        setCurrentDesktop(newTargetIndex);
        appRefs.current = {};
        resetDrag();
    };

    // Check edge drag
    const checkEdgeDrag = (x) => {
        const screenWidth = window.innerWidth;
        const edgeZone = 50;

        if (x < edgeZone && currentDesktop > 0) {
            // Left edge - go to previous desktop
            if (edgeDragZone !== 'left') {
                setEdgeDragZone('left');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop - 1);
                    }
                }, 800);
            }
        } else if (x > screenWidth - edgeZone) {
            // Right edge - go to next desktop or create new one
            if (edgeDragZone !== 'right') {
                setEdgeDragZone('right');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        // Allow creating new desktop by moving to currentDesktop + 1
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop + 1);
                    }
                }, 800);
            }
        } else {
            if (edgeDragZone) {
                setEdgeDragZone(null);
                if (edgeDragTimer.current) {
                    clearTimeout(edgeDragTimer.current);
                    edgeDragTimer.current = null;
                }
            }
        }
    };

    // Navigate between desktops
    const goToDesktop = (index) => {
        if (index >= 0 && index < desktops.length) {
            setCurrentDesktop(index);
            appRefs.current = {};
        }
    };

    // Swipe handlers
    const swipeStartRef = useRef(null);
    const handleGridTouchStart = (e) => {
        if (touchDragInfo) return;
        swipeStartRef.current = { x: e.touches[0].clientX, time: Date.now() };
    };
    const handleGridTouchMove = (e) => {
        if (swipeStartRef.current && !touchDragInfo) {
            swipeStartRef.current.lastX = e.touches[0].clientX;
        }
    };
    const handleGridTouchEnd = (e) => {
        if (touchDragInfo || !swipeStartRef.current) return;
        const endX = e.changedTouches?.[0]?.clientX || swipeStartRef.current.lastX || swipeStartRef.current.x;
        const diff = endX - swipeStartRef.current.x;
        const timeDiff = Date.now() - swipeStartRef.current.time;

        if (timeDiff < 500 && Math.abs(diff) > 80) {
            if (diff > 0 && currentDesktop > 0) {
                goToDesktop(currentDesktop - 1);
            } else if (diff < 0 && currentDesktop < desktops.length - 1) {
                goToDesktop(currentDesktop + 1);
            }
        }
        swipeStartRef.current = null;
    };

    // ========== TOUCH DRAG & DROP ==========

    const handleTouchStart = (e, position) => {
        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY, position };

        longPressTimer.current = setTimeout(() => {
            const appId = gridApps[position];
            dragItem.current = { position };
            setDraggedId(appId);
            setTouchDragInfo({
                appId,
                startX: touch.clientX,
                startY: touch.clientY,
                currentX: touch.clientX,
                currentY: touch.clientY
            });
            if (navigator.vibrate) navigator.vibrate(50);
        }, 300);
    };

    const handleTouchMove = (e) => {
        const touch = e.touches[0];

        if (longPressTimer.current && touchStartPos.current) {
            const dx = Math.abs(touch.clientX - touchStartPos.current.x);
            const dy = Math.abs(touch.clientY - touchStartPos.current.y);
            if (dx > 10 || dy > 10) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }

        if (touchDragInfo) {
            setTouchDragInfo(prev => ({
                ...prev,
                currentX: touch.clientX,
                currentY: touch.clientY
            }));
            checkEdgeDrag(touch.clientX);
            const targetInfo = findDropTarget(touch.clientX, touch.clientY);
            if (targetInfo) {
                dragOverItem.current = targetInfo;
                setDropTarget(targetInfo);
            }
        }
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }

        if (touchDragInfo && dragItem.current && dragOverItem.current) {
            performDrop();
        } else if (touchDragInfo) {
            resetDrag();
        }

        touchStartPos.current = null;
        setTouchDragInfo(null);
    };

    const findDropTarget = (x, y) => {
        for (const [key, ref] of Object.entries(appRefs.current)) {
            if (!ref) continue;
            const rect = ref.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                const [, indexStr] = key.split('-');
                const position = parseInt(indexStr, 10);
                const midX = rect.left + rect.width / 2;
                const side = x < midX ? 'left' : 'right';
                return { position, side };
            }
        }

        const gridEl = document.querySelector('[data-grid="true"]');
        if (gridEl) {
            const rect = gridEl.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                return { position: gridApps.length, side: 'left' };
            }
        }

        return null;
    };

    const performDrop = () => {
        if (!dragItem.current) {
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current || { position: 0, side: 'left' };

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        if (from.position === insertPosition || from.position === insertPosition - 1) {
            resetDrag();
            return;
        }

        let newDesktops = desktops.map(d => [...d]);
        let newGridApps = [...newDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        newDesktops[currentDesktop] = newGridApps;
        setDesktops(newDesktops);
        saveToDb(newDesktops);
        resetDrag();
    };

    // Context menu state (for removing icons)
    const [contextMenu, setContextMenu] = useState(null); // { itemId, x, y, index }

    // Remove item from dashboard (not from pinnedItems, just from current layout)
    const removeItemFromDashboard = (index) => {
        let newDesktops = desktops.map(d => [...d]);
        newDesktops[currentDesktop] = newDesktops[currentDesktop].filter((_, i) => i !== index);

        // Remove empty desktops (except first one)
        newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
        if (newDesktops.length === 0) newDesktops = [[]];

        setDesktops(newDesktops);
        saveToDb(newDesktops);
        setContextMenu(null);

        // Adjust current desktop if needed
        if (currentDesktop >= newDesktops.length) {
            setCurrentDesktop(Math.max(0, newDesktops.length - 1));
        }
    };

    // Handle right-click / long press to show context menu
    const handleContextMenu = (e, itemId, index) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ itemId, x: e.clientX || e.touches?.[0]?.clientX, y: e.clientY || e.touches?.[0]?.clientY, index });
    };

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        if (contextMenu) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [contextMenu]);

    // Background style
    const bgStyle = currentBg.image
        ? { backgroundImage: `url(${currentBg.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: currentBg.gradient };

    // Check if touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Render app icon (grid only - modules and groups)
    const renderAppIcon = (itemId, index) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        const { type, id } = parsed;
        const isDragging = draggedId === itemId;
        const refKey = `grid-${index}`;

        const isDropLeft = dropTarget && dropTarget.position === index && dropTarget.side === 'left';
        const isDropRight = dropTarget && dropTarget.position === index && dropTarget.side === 'right';

        if (type === 'module') {
            const navId = id;
            const code = NAV_TO_CODE[navId];
            const Icon = MODULE_ICONS[code] || LayoutGrid;
            const color = MODULE_COLORS[code] || '#8b5cf6';
            const hasBadge = navId === 'messages' && unreadMessages > 0;

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
                >
                    {isDropLeft && draggedId !== itemId && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={!isTouchDevice}
                        onDragStart={!isTouchDevice ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={!isTouchDevice ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && onNavigate(navId)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none touch-none
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10 active:scale-90'}`}
                    >
                        <div
                            className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative"
                            style={{ backgroundColor: color }}
                        >
                            <Icon className="w-7 h-7 text-white" />
                            {hasBadge && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {getModuleLabel(navId)}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        } else if (type === 'group') {
            const group = getGroupById(id);
            if (!group) return null;

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
                >
                    {isDropLeft && draggedId !== itemId && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={!isTouchDevice}
                        onDragStart={!isTouchDevice ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={!isTouchDevice ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && setOpenGroupPopup(group)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none touch-none
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10 active:scale-90'}`}
                    >
                        <div
                            className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative"
                            style={{ backgroundColor: group.color || '#8b5cf6' }}
                        >
                            <Folder className="w-7 h-7 text-white" />
                            {group.modules?.length > 0 && (
                                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white text-gray-700 text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                    {group.modules.length}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {group.name}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        }

        return null;
    };

    // Get icon for floating drag
    const getDragIcon = (itemId) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        if (parsed.type === 'module') {
            const code = NAV_TO_CODE[parsed.id];
            const Icon = MODULE_ICONS[code] || LayoutGrid;
            const color = MODULE_COLORS[code] || '#8b5cf6';
            return { Icon, color };
        } else if (parsed.type === 'group') {
            const group = getGroupById(parsed.id);
            return { Icon: Folder, color: group?.color || '#8b5cf6' };
        }
        return null;
    };

    // Handle drag over the entire dashboard (for edge detection)
    const handleDashboardDragOver = (e) => {
        e.preventDefault();
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    // Handle mouse move for edge hover detection (show arrows when near edges)
    const handleMouseMove = useCallback((e) => {
        const screenWidth = window.innerWidth;
        const edgeZone = 60;
        const x = e.clientX;

        // Check left edge - only show if not on first desktop
        if (x < edgeZone && currentDesktop > 0) {
            setEdgeHover('left');
        }
        // Check right edge - only show if there's a next desktop
        else if (x > screenWidth - edgeZone && currentDesktop < desktops.length - 1) {
            setEdgeHover('right');
        }
        else {
            setEdgeHover(null);
        }
    }, [currentDesktop, desktops.length]);

    // Handle click on edge zones to navigate between desktops
    const handleEdgeClick = (direction) => {
        if (direction === 'left' && currentDesktop > 0) {
            goToDesktop(currentDesktop - 1);
        } else if (direction === 'right' && currentDesktop < desktops.length - 1) {
            goToDesktop(currentDesktop + 1);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 overflow-hidden"
            style={bgStyle}
            onDragOver={handleDashboardDragOver}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setEdgeHover(null)}
        >
            {/* MouseGlow cursor */}
            <MouseGlow />

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/20" />

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full">
                {/* Header - Logo centered with language switcher and logout button */}
                <div className="flex items-center justify-center gap-3 px-6 py-4">
                    <DashboardLanguageSwitcher />
                    <div className="bg-white rounded-2xl px-4 py-2 shadow-lg">
                        <img src={logo} alt="Cabaza" className="h-8" />
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        title={t.menu?.logout || 'Logout'}
                    >
                        <LogOut className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* App Grid - 4x3 = 12 icons */}
                <div
                    className="flex-1 px-4 py-4 overflow-y-auto"
                    data-grid="true"
                    onDragOver={handleContainerDragOver}
                    onDrop={handleDrop}
                    onTouchStart={handleGridTouchStart}
                    onTouchMove={handleGridTouchMove}
                    onTouchEnd={handleGridTouchEnd}
                >
                    {gridApps.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-white/60">
                            <LayoutGrid className="w-16 h-16 mb-4 opacity-50" />
                            <p className="text-lg font-medium">{t.dashboard?.noPinnedItems || 'No pinned items'}</p>
                            <p className="text-sm mt-1">{t.dashboard?.pinFromModules || 'Pin modules from the Modules page'}</p>
                            <button
                                onClick={() => onNavigate('modules')}
                                className="mt-4 px-4 py-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                            >
                                {t.menu?.modules || 'Modules'}
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto">
                            {gridApps.map((itemId, index) => renderAppIcon(itemId, index))}
                        </div>
                    )}
                </div>

                {/* Fixed Dock */}
                <div className="px-4 pb-4">
                    <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-3 max-w-sm mx-auto">
                        <div className="flex justify-around items-center">
                            {DOCK_APPS.map((app) => {
                                const Icon = app.icon;
                                return (
                                    <button
                                        key={app.id}
                                        onClick={() => onNavigate(app.navId)}
                                        className="flex flex-col items-center gap-1 p-2 rounded-2xl hover:bg-white/10 active:scale-90 transition-all"
                                    >
                                        <div
                                            className="w-12 h-12 rounded-2xl shadow-lg flex items-center justify-center"
                                            style={{ backgroundColor: app.color }}
                                        >
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Desktop indicators */}
                <div className="flex justify-center gap-2 py-2">
                    {desktops.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToDesktop(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                                index === currentDesktop
                                    ? 'bg-white w-6'
                                    : 'bg-white/40 hover:bg-white/60'
                            }`}
                        />
                    ))}
                    {draggedId && (
                        <div className="w-2 h-2 rounded-full bg-white/20 border border-white/40" />
                    )}
                </div>

                {/* Floating drag icon for touch */}
                {touchDragInfo && (() => {
                    const iconInfo = getDragIcon(touchDragInfo.appId);
                    if (!iconInfo) return null;
                    const { Icon, color } = iconInfo;
                    return (
                        <div
                            className="fixed pointer-events-none z-[9999]"
                            style={{
                                left: touchDragInfo.currentX - 28,
                                top: touchDragInfo.currentY - 28,
                            }}
                        >
                            <div
                                className="w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center opacity-90 scale-110"
                                style={{ backgroundColor: color }}
                            >
                                <Icon className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    );
                })()}

                {/* Edge zone indicators - show on drag OR hover */}
                {/* Left edge - show if not on first desktop */}
                {currentDesktop > 0 && (
                    <div
                        className={`fixed left-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center transition-all cursor-pointer ${
                            edgeDragZone === 'left' || edgeHover === 'left' ? 'bg-white/20' : 'bg-transparent'
                        }`}
                        onClick={() => handleEdgeClick('left')}
                    >
                        <ChevronLeft className={`w-10 h-10 text-white transition-all ${
                            edgeDragZone === 'left' ? 'opacity-100 animate-pulse scale-125' :
                            edgeHover === 'left' ? 'opacity-80 scale-110' : 'opacity-0'
                        }`} />
                    </div>
                )}

                {/* Right edge - show if there's another desktop */}
                {currentDesktop < desktops.length - 1 && (
                    <div
                        className={`fixed right-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center transition-all cursor-pointer ${
                            edgeDragZone === 'right' || edgeHover === 'right' ? 'bg-white/20' : 'bg-transparent'
                        }`}
                        onClick={() => handleEdgeClick('right')}
                    >
                        <ChevronRight className={`w-10 h-10 text-white transition-all ${
                            edgeDragZone === 'right' ? 'opacity-100 animate-pulse scale-125' :
                            edgeHover === 'right' ? 'opacity-80 scale-110' : 'opacity-0'
                        }`} />
                    </div>
                )}

                {/* Home indicator */}
                <div className="flex justify-center pb-2">
                    <div className="w-32 h-1 bg-white/50 rounded-full" />
                </div>
            </div>

            {/* Context Menu for removing icons */}
            {contextMenu && (
                <div
                    className="fixed z-[9999] bg-white rounded-xl shadow-2xl py-2 min-w-[140px] animate-in fade-in zoom-in-95 duration-150"
                    style={{
                        left: Math.min(contextMenu.x, window.innerWidth - 160),
                        top: Math.min(contextMenu.y, window.innerHeight - 60)
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={() => removeItemFromDashboard(contextMenu.index)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors"
                    >
                        <Minus className="w-4 h-4" />
                        <span className="font-medium">{t.dashboard?.removeIcon || 'Retirer'}</span>
                    </button>
                </div>
            )}

            {/* Group Popup Modal */}
            {openGroupPopup && (
                <GroupPopupModal
                    group={openGroupPopup}
                    onClose={() => setOpenGroupPopup(null)}
                    onNavigate={onNavigate}
                    unreadMessages={unreadMessages}
                    t={t}
                    language={language}
                />
            )}
        </div>
    );
};

export default Dashboard;
